import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { getWindow } from '../../../utils/getWindow';
import { Box } from '../Box';
import { CHECK_DELAY, LOAD_MORE_DELAY } from './constants';
import { VirtualAdaptiveGridColumn, VirtualAdaptiveGridFlex } from './styles';
import { VirtualAdaptiveGridProps } from './typings';
import { getDefaultRenderIndexes, getMinHeghtColumnIndex, positiveOrZero } from './utils';

export const VirtualAdaptiveGrid = <ItemType extends { id: string }>({
    items,
    columns: columnsNumber,
    indexOffset = 4,
    itemsXMargin = 0,
    itemsYMargin = 0,
    topYMargin = 0,
    bottomYMargin = 0,
    itemWidth: itemWidthBase = 0,
    renderItem,
    getItemHeight,
    loadMore,
    containerRef,
    ...other
}: VirtualAdaptiveGridProps<ItemType>) => {
    const columns = useRef<(ItemType & { trueIndex: number })[][]>([]);
    const [itemWidth, setItemWidth] = useState(itemWidthBase);

    const [renderIndexes, setRenderIndexes] = useState<Array<{ start: number; end: number }>>([]);
    const lastRenderIndexesRef = useRef<Array<{ start: number; end: number }>>([]);

    const mainContainerRef = useRef<HTMLDivElement>(null);
    const columnsHeightMap = useRef<number[][]>([]);
    const topOffsetRef = useRef(0);
    const wasSizesSavedRef = useRef(false);
    const wasDefaultIndexesSetedRef = useRef(false);
    const lastScrollTopRef = useRef(0);
    const loadNextItemsMapRef = useRef<Record<number, boolean>>({});

    const visibleMapRef = useRef<Map<HTMLElement, boolean>>(new Map());
    // eslint-disable-next-line no-spaced-func
    const trueIndexToVisibleRef = useRef<Record<number, () => boolean | undefined>>({});
    const observerCbRef = useRef<IntersectionObserverCallback>((entries) => {
        entries.forEach((entry) =>
            visibleMapRef.current.set(entry.target as HTMLElement, entry.isIntersecting),
        );
    });
    const observerRef = useRef(new IntersectionObserver(observerCbRef.current, { threshold: 0.5 }));

    const realGridHeightRef = useRef(window.innerHeight);

    const checkRenderIndexes = useRef<(staticScroll?: boolean) => void>((staticScroll = false) => {
        const containerElement = containerRef?.current || document.documentElement;
        const scrollTop = containerElement.scrollTop - topOffsetRef.current - topYMargin;
        const clientHeight = containerElement.clientHeight;

        const newIndexes = lastRenderIndexesRef.current.map((columnRenderIndexes, columnIndex) => {
            let newStart = columnRenderIndexes.start;
            let newEnd = columnRenderIndexes.end;

            // скролл вниз
            if (scrollTop > lastScrollTopRef.current || staticScroll) {
                /**
                 * Пока нижньий item не начнет вписываться в размеры - будем его увеличивать,
                 * намеренно не ставим в while чек верхнего item-a, тк при скролле вниз
                 * его не будет видно.
                 * */
                while (
                    scrollTop + clientHeight >
                    columnsHeightMap.current[columnIndex][positiveOrZero(newEnd - indexOffset)] +
                        topYMargin
                ) {
                    // Если зашли в while, значит двигаем индекс нижнего item-a
                    newEnd++;

                    // Проверка верхнего item-a
                    if (
                        scrollTop >
                        columnsHeightMap.current[columnIndex][newStart + indexOffset] + topYMargin
                    ) {
                        newStart++;
                    }
                }
            }

            // скролл вверх
            if (scrollTop < lastScrollTopRef.current) {
                /**
                 * Пока верхний item не начнет вписываться в размеры - будем его уменьшать,
                 * намеренно не ставим в while чек нижнего item-a, тк при скролле вверх
                 * его не будет видно.
                 * */
                while (
                    scrollTop <
                        columnsHeightMap.current[columnIndex][newStart + indexOffset] +
                            topYMargin &&
                    newStart >= 1
                ) {
                    newStart--;

                    if (newStart < 0) {
                        newStart = 0;
                    }

                    // Проверка нижнего item-a
                    if (
                        scrollTop + clientHeight <
                        columnsHeightMap.current[columnIndex][
                            positiveOrZero(newEnd - indexOffset)
                        ] +
                            topYMargin
                    ) {
                        newEnd--;
                    }
                }
            }

            return { start: newStart, end: newEnd };
        });

        lastScrollTopRef.current = scrollTop;
        lastRenderIndexesRef.current = newIndexes;

        setRenderIndexes(newIndexes);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onLoadTrottled = useCallback(
        throttle(() => {
            const wasLoadedByLength = loadNextItemsMapRef.current[items.length];

            if (!wasLoadedByLength) {
                loadNextItemsMapRef.current[items.length] = true;
                loadMore();
            }
        }, LOAD_MORE_DELAY),
        [loadMore, items.length],
    );

    // Считаем отступ от верхнего края страницы до контейнера
    useLayoutEffect(() => {
        if (!wasSizesSavedRef.current) {
            wasSizesSavedRef.current = true;
            topOffsetRef.current = getWindow()?.innerHeight || 0;
        }
    }, []);

    // Выставляем ширину итема
    useLayoutEffect(() => {
        if (!itemWidth && mainContainerRef.current && mainContainerRef.current.clientWidth !== 0) {
            const width = Number(
                (mainContainerRef.current.clientWidth / columnsNumber - itemsYMargin).toFixed(2),
            );

            setItemWidth(width);
        }
    }, [itemWidth, columnsNumber, itemsYMargin]);

    useEffect(() => {
        if (!items.length || !itemWidth || wasDefaultIndexesSetedRef.current) {
            return;
        }

        wasDefaultIndexesSetedRef.current = true;
        const defaultIndexes = getDefaultRenderIndexes(itemWidth, columnsNumber, containerRef);

        setRenderIndexes(defaultIndexes);
        lastRenderIndexesRef.current = defaultIndexes;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length, itemWidth, containerRef]);

    useLayoutEffect(() => {
        const onScroll = throttle(() => {
            checkRenderIndexes.current();
        }, CHECK_DELAY);
        const containerElement = containerRef?.current || document;

        containerElement.addEventListener('scroll', onScroll);

        return () => {
            containerElement.removeEventListener('scroll', onScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef?.current]);

    const lastItemAddedToColumnsIndexRef = useRef(0);

    useEffect(() => {
        if (!items.length || !itemWidth) {
            return;
        }

        // При удалении элементов из сетки надо пересчитывать размеры карточек
        if (items.length <= lastItemAddedToColumnsIndexRef.current) {
            columns.current = [];
            columnsHeightMap.current = [];
        }

        if (!columns.current.length) {
            for (let i = 0; i < columnsNumber; i++) {
                columns.current.push([
                    {
                        ...items[i],
                        trueIndex: i,
                    },
                ]);
                columnsHeightMap.current[i] = [0];
            }

            lastItemAddedToColumnsIndexRef.current = columnsNumber - 1;
        }

        for (let i = lastItemAddedToColumnsIndexRef.current + 1; i < items.length; i++) {
            const minHeightColumn = getMinHeghtColumnIndex(columnsHeightMap.current);

            columns.current[minHeightColumn].push({
                ...items[i],
                trueIndex: i,
            });

            const currentHeight = Number(
                (
                    getItemHeight(
                        columns.current[minHeightColumn][
                            columns.current[minHeightColumn].length - 2
                        ].trueIndex,
                        itemWidth,
                    ) +
                    itemsXMargin +
                    (columnsHeightMap.current[minHeightColumn][
                        columnsHeightMap.current[minHeightColumn].length - 1
                    ] || 0)
                ).toFixed(2),
            );

            columnsHeightMap.current[minHeightColumn].push(currentHeight);
        }

        lastItemAddedToColumnsIndexRef.current = items.length - 1;
        checkRenderIndexes.current(true);

        const maxHeight = columns.current.reduce((max, column, index) => {
            const lastItemIndex = columnsHeightMap.current[index].length - 1;
            const columnHeight = columnsHeightMap.current[index][lastItemIndex];
            const lastItemHeight = getItemHeight(column[column.length - 1].trueIndex, itemWidth);

            return Math.max(max, columnHeight + lastItemHeight);
        }, 0);

        realGridHeightRef.current = maxHeight;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length, columnsNumber, itemWidth, containerRef]);

    useEffect(() => {
        if (columns.current.length > 0) {
            // таймаут нужен для первичного рендера сетки и установки высоты, иначе при вычислении видимых индексов будет некорректный scrollTop
            const timer = setTimeout(() => {
                checkRenderIndexes.current(true);
            }, 50);

            return () => clearTimeout(timer);
        }
    }, [items.length, columnsNumber]);

    useEffect(() => () => observerRef.current.disconnect(), []);

    const renderColumns: (ItemType & { trueIndex: number })[][] = [];
    const shouldLoadMore = useRef(false);

    if (renderIndexes.length) {
        for (let i = 0; i < columns.current.length; i++) {
            renderColumns.push(
                columns.current[i].slice(renderIndexes[i].start, renderIndexes[i].end),
            );

            if (!columns.current[i][renderIndexes[i].end + 1]) {
                shouldLoadMore.current = true;
            }
        }
    }

    // Move loadMore call to useEffect to avoid calling during render
    useEffect(() => {
        if (shouldLoadMore.current) {
            shouldLoadMore.current = false;
            onLoadTrottled();
        }
    });

    return (
        <VirtualAdaptiveGridFlex
            {...other}
            ref={mainContainerRef}
            height={realGridHeightRef.current}
            width={`${
                itemWidth * columns.current.length + itemsYMargin * (columns.current.length - 1)
            }px`}
        >
            {renderColumns.map((column, columnIndex) => {
                const lastColumnItem = column[column.length - 1];
                const gapTop =
                    bottomYMargin && lastColumnItem
                        ? (columnsHeightMap.current[columnIndex][
                              columnsHeightMap.current[columnIndex].length - 1
                          ] || 0) +
                          getItemHeight(lastColumnItem.trueIndex, itemWidth) +
                          topYMargin
                        : 0;

                return (
                    <VirtualAdaptiveGridColumn
                        key={columnIndex}
                        width={itemWidth}
                        mr={columnIndex < renderColumns.length - 1 ? 1 : 0}
                    >
                        {topYMargin ? (
                            <Box position="absolute" height={topYMargin} top="0px" width="100%" />
                        ) : null}
                        {column.map((item, itemIndex) => {
                            const currentItemIndex = itemIndex + renderIndexes[columnIndex].start;
                            const currentHeight = columnsHeightMap.current[columnIndex][
                                currentItemIndex + 1
                            ]
                                ? columnsHeightMap.current[columnIndex][currentItemIndex + 1] -
                                  columnsHeightMap.current[columnIndex][currentItemIndex]
                                : getItemHeight(item.trueIndex, itemWidth);

                            const isVisible = trueIndexToVisibleRef.current[item.trueIndex]?.();

                            return (
                                <Box
                                    ref={(node) => {
                                        if (node && !visibleMapRef.current.get(node)) {
                                            visibleMapRef.current.set(node, false);
                                            trueIndexToVisibleRef.current[item.trueIndex] = () =>
                                                visibleMapRef.current.get(node);

                                            observerRef.current.observe(node);
                                        }
                                    }}
                                    key={currentItemIndex}
                                    width={`${itemWidth}px`}
                                    height={`${currentHeight}px`}
                                    position="absolute"
                                    top={`${
                                        columnsHeightMap.current[columnIndex][currentItemIndex] +
                                        topYMargin
                                    }px`}
                                >
                                    {renderItem(item.trueIndex, isVisible, {
                                        width: itemWidth,
                                        height: currentHeight,
                                    })}
                                </Box>
                            );
                        })}
                        {bottomYMargin ? (
                            <Box
                                position="absolute"
                                height={bottomYMargin}
                                top={`${gapTop}px`}
                                width="100%"
                            />
                        ) : null}
                    </VirtualAdaptiveGridColumn>
                );
            })}
        </VirtualAdaptiveGridFlex>
    );
};
